import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { formatCr, GATE_LABELS, TEMPERATURE_COLORS, HEALTH_COLORS, formatDate, MONEY_UNIT_LABEL } from "@/lib/utils-ros";
import { GateFunnel } from "@/components/revenueos/GateFunnel";
import { ActionEditor } from "@/components/revenueos/ActionEditor";
import { MeetingLogInline } from "@/components/revenueos/MeetingLog";
import { TeamOwnershipTable } from "@/components/revenueos/TeamOwnershipTable";
import { BuyingCommitteeEditor } from "@/components/revenueos/BuyingCommittee";
import { ShareModal } from "@/components/revenueos/ShareModal";
import { ArrowLeft, Sparkles, AlertTriangle, Loader2, CheckSquare, Package, Users2, Layers, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CustomerCard } from "@/components/revenueos/CustomerCard";
import { LineItemsPanel } from "@/components/revenueos/LineItemsPanel";
import { PartnerAssignmentPanel } from "@/components/revenueos/PartnerAssignment";
import { useAuth } from "@/lib/auth";
import { RequestDeletionModal } from "@/components/revenueos/RequestDeletionModal";

// safe text renderer
const asText = (a) => {
  if (typeof a === "string") return a;
  if (a && typeof a === "object") {
    const owner = a.owner || a.who;
    const action = a.action || a.title || a.task || a.factor || a.risk || "";
    return `${action}${owner ? ` — ${owner}` : ""}`;
  }
  return String(a ?? "");
};

export default function OpportunityDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { canWrite, canEditOpportunities, isSuperAdmin, isAdmin, isFounder } = useAuth();
  const canDeleteDirect = isSuperAdmin || isAdmin || isFounder;
  const canEditOpp = canEditOpportunities();
  const canBookOrder = canWrite("orders");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gateBusy, setGateBusy] = useState(false);
  const [booking, setBooking] = useState(false);
  const [deletionModalOpen, setDeletionModalOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [teamRefreshKey, setTeamRefreshKey] = useState(0);
  const [customerOpen, setCustomerOpen] = useState(false);

  function goBack() {
    if (location.state?.from) {
      nav(location.state.from);
      return;
    }
    if (document.referrer.startsWith(window.location.origin)) {
      nav(-1);
      return;
    }
    nav("/opportunities");
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const { data: payload } = await api.get(`/opportunities/${id}`);
      setData(payload);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : status === 403
          ? "You don't have permission to view this opportunity."
          : status === 404
            ? "Opportunity not found."
            : "Could not load opportunity.";
      setError({ status, message });
      setData(null);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [id]);

  async function advanceGate(gate) {
    if (gateBusy) return;
    setGateBusy(true);
    try {
      await api.patch(`/opportunities/${id}`, { current_gate: gate });
      toast.success(`Moved to ${GATE_LABELS[gate]}`);
      await load();
    } catch { toast.error("Update failed"); }
    finally { setGateBusy(false); }
  }

  async function bookOrder() {
    if (!confirm("Book this deal as an order? This marks the opportunity as WON.")) return;
    setBooking(true);
    try {
      await api.post(`/opportunities/${id}/create-order`, {});
      toast.success("Order booked — opportunity marked WON");
      nav("/orders");
    } catch { toast.error("Failed to book order"); }
    finally { setBooking(false); }
  }

  async function deleteOpp() {
    const liveOrders = (o.line_items || []).filter(li => li.order_id).length;
    const msg = liveOrders
      ? `Delete this opportunity?\n\n⚠️ ${liveOrders} order(s) already exist against this deal. Deleting will hide the opportunity from all lists — the orders will remain intact.\n\nYou can restore it using the Lost button on the right side of the pipeline bar.`
      : `Delete this opportunity?\n\nIt will be moved to Lost. You can restore it using the Lost button on the right side of the pipeline bar.`;
    if (!confirm(msg)) return;
    try {
      await api.delete(`/opportunities/${id}?reason=user_deleted`);
      toast.success("Opportunity moved to Lost · restore from the pipeline bar");
      nav("/opportunities");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  // Only Super Admin / Admin delete directly — every other role raises a deletion
  // request that lands in the Admin/Founder Requests queue instead.
  function handleDeleteClick() {
    if (canDeleteDirect) {
      deleteOpp();
    } else {
      setDeletionModalOpen(true);
    }
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-[var(--text-muted)]">
        <Loader2 size={16} className="animate-spin" /> Loading opportunity…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-full bg-[#F7FAFC] px-4 md:px-6 py-6" data-testid="opp-detail-error">
        <button type="button" onClick={goBack} className="inline-flex items-center gap-2 text-[12px] font-medium text-[#07539A] hover:text-[#063F73] mb-6">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="max-w-lg rounded-[20px] border border-[#E7B8B8] bg-[#FFF8F7] p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-[var(--danger)] shrink-0 mt-0.5" />
            <div>
              <h1 className="font-display text-lg text-[#12324A]">
                {error.status === 403 ? "Access denied" : error.status === 404 ? "Not found" : "Could not load"}
              </h1>
              <p className="text-[13px] text-[#607D8B] mt-2">{error.message}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;
  const { opportunity: o, company, partner, tasks } = data;

  const openTasks = tasks.filter(t => !["completed", "verified", "cancelled"].includes(t.status));
  const returnLabel = location.state?.fromLabel || "Opportunities";

  return (
    <div className="min-h-full bg-[#F7FAFC] px-4 md:px-6 py-6 space-y-6 opportunity-detail-content" data-testid="opp-detail-page">
      <button type="button" onClick={goBack} data-testid="back-to-opps" className="inline-flex items-center gap-2 text-[12px] font-medium text-[#07539A] hover:text-[#063F73]">
        <ArrowLeft size={14} /> Back to {returnLabel}
      </button>

      {/* Customer hero, metrics and gate progression */}
      <div className="overflow-hidden rounded-[26px] border border-[#BBD8E7] bg-white shadow-[0_14px_34px_rgba(7,83,154,0.11)]">
        <div className="bg-[#07539A] px-5 md:px-7 py-6 flex flex-col lg:flex-row lg:items-start justify-between gap-5">
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-2xl md:text-3xl tracking-tight !text-white truncate">{company?.name || "Opportunity"}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-white/75">
              <span className="!text-white/80">{company?.industry || "Industry not set"}</span>
              <span className="text-white/40">·</span>
              <span className="!text-white/80">{partner?.firm || "Direct"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap lg:justify-end">
            <button
              onClick={() => setCustomerOpen(true)}
              data-testid="customer-360-btn"
              title="See all customer history across every stage"
              className="h-10 px-4 rounded-xl bg-[#DDF2FB] hover:bg-white !text-[#07539A] text-[12px] font-semibold flex items-center gap-1.5 shadow-[0_6px_16px_rgba(0,0,0,0.10)]"
            >
              <Layers size={13} /> Customer 360
            </button>
            {canBookOrder && o.status !== "won" && (
              <button
                onClick={bookOrder}
                disabled={booking}
                data-testid="send-to-order-btn"
                title="Book this deal as an order at any funnel stage"
                className="h-10 px-4 rounded-xl bg-[#E28B21] hover:bg-[#C97715] disabled:opacity-50 !text-white text-[12px] font-semibold flex items-center gap-2 shadow-[0_6px_16px_rgba(226,139,33,0.28)]"
              >
                {booking ? <Loader2 size={13} className="animate-spin" /> : <Package size={13} />}
                Send to Order
              </button>
            )}
            {o.status === "won" && (
              <span className="text-[11px] uppercase tracking-widest px-2 py-1 rounded-sm bg-[var(--success)]/10 text-[var(--success)] border border-[var(--success)]/25">Won</span>
            )}
            {canEditOpp && (
            <button
              onClick={handleDeleteClick}
              data-testid="delete-opp-btn"
              title={canDeleteDirect ? "Delete this opportunity" : "Request deletion"}
              className="h-10 px-3 rounded-xl border border-[#F4B6B6] bg-white hover:bg-[#FFF5F5] text-[#B42318] text-[12px] font-medium flex items-center gap-1.5 shadow-[0_5px_14px_rgba(0,0,0,0.08)]"
            >
              <Trash2 size={12} /> {canDeleteDirect ? "Delete" : "Request deletion"}
            </button>
            )}
          </div>
        </div>

        <div className="p-4 md:p-6 bg-white">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: `Value (${MONEY_UNIT_LABEL})`, value: formatCr(o.value_cr) },
              { label: "Probability", value: `${o.probability}%` },
              { label: "Deal health", value: String(o.health || "—").replaceAll("_", " "), color: HEALTH_COLORS[o.health] },
              { label: "Buyer engagement", value: o.temperature || "—", color: TEMPERATURE_COLORS[o.temperature] },
              { label: "Risk score", value: o.risk_score ?? "—" },
              { label: "Close date", value: formatDate(o.forecast_close_date) },
            ].map(metric => (
              <div key={metric.label} className="min-h-[92px] rounded-[18px] border border-[#C9DCE6] bg-[#F3FAFD] px-4 py-3 shadow-[0_6px_16px_rgba(7,83,154,0.06)]">
                <div className="text-[9px] uppercase tracking-widest font-semibold text-[#607D8B]">{metric.label}</div>
                <div className="font-display text-xl tabular-nums mt-2 capitalize text-[#07539A]" style={metric.color ? { color: metric.color } : undefined}>{metric.value}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-[20px] border border-[#D7E8F0] bg-[#F8FCFE] p-4 md:p-5">
            <div className="font-display text-[16px] text-[#12324A] mb-3">Gate progression</div>
            <GateFunnel currentGate={o.current_gate} onGateClick={canEditOpp ? advanceGate : undefined} variant="detail" />
          </div>
        </div>
      </div>

      {/* Action workspace + Buying Committee */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.75fr)_minmax(340px,1fr)] gap-5 items-start min-w-0">
        <div className="space-y-5 min-w-0">
          <section className="rounded-[24px] border border-[#BBD8E7] bg-white overflow-hidden shadow-[0_10px_26px_rgba(7,83,154,0.08)]" data-testid="action-zone">
            <div className="px-3 py-2 bg-[#07539A] border-b border-[#063F73] flex items-center gap-2">
              <Sparkles size={13} className="!text-white" />
              <h2 className="text-[11px] uppercase tracking-widest !text-white font-semibold">Action Zone</h2>
            </div>
            <div className="p-4 space-y-3">
              <ActionEditor
                readOnly={!canEditOpp}
                actions={o.ai_next_actions || []}
                aiInsights={o.ai_summary}
                title="Actions to advance this deal"
                aiTitle="Niq's deal insights"
                accent="navy"
                rounded
                insightFirst
                insightActionLabel="Generate"
                insightMarkdown
                insightAnchorId="opp-ai-insights"
                onSave={canEditOpp ? async (arr) => {
                  const { data } = await api.patch(`/opportunities/${o.id}`, { ai_next_actions: arr });
                  setData((d) => d ? { ...d, opportunity: { ...d.opportunity, ai_next_actions: arr } } : d);
                  return data;
                } : undefined}
                onRegenerate={canEditOpp ? async () => {
                  const { data } = await api.post(`/opportunities/${o.id}/coach`);
                  const summary = data.coaching_advice || data.ai_summary;
                  const nextActions = data.next_best_actions || data.ai_next_actions;
                  setData((d) => d ? {
                    ...d,
                    opportunity: {
                      ...d.opportunity,
                      ai_summary: summary,
                      ...(nextActions?.length ? { ai_next_actions: nextActions } : {}),
                      ...(data.health_assessment ? { health: data.health_assessment } : {}),
                      ...(data.temperature ? { temperature: data.temperature } : {}),
                      ...(data.probability != null ? { probability: data.probability } : {}),
                      ...(data.risk_score != null ? { risk_score: data.risk_score } : {}),
                      ...(data.risk_factors != null ? { ai_risk_factors: data.risk_factors } : {}),
                    },
                  } : d);
                  toast.success("Deal coached");
                  return { ai_summary: summary, ai_next_actions: nextActions };
                } : undefined}
              />

              {o.ai_risk_factors?.length > 0 && (
                <div className="border border-[#E7B8B8] bg-[#FFF8F7] rounded-[18px] p-4" data-testid="ai-risks">
                  <div className="text-[11px] uppercase tracking-widest text-[var(--danger)] mb-2 flex items-center gap-2">
                    <AlertTriangle size={12} /> Niq's Risk Factor Analysis
                  </div>
                  <div className="space-y-1.5">
                    {o.ai_risk_factors.map((r, i) => (
                      <div key={i} className="text-[13px] text-[var(--text-muted)]">· {asText(r)}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          <LineItemsPanel oppId={o.id} oppValue={o.value_cr} oppStatus={o.status} onChanged={load} readOnly={!canEditOpp} />

          <section className="rounded-[24px] border border-[#BBD8E7] bg-white overflow-hidden shadow-[0_10px_26px_rgba(7,83,154,0.08)]" data-testid="team-ownership-zone">
            <div className="px-3 py-2 bg-[#07539A] border-b border-[#063F73] flex items-center gap-2">
              <Users2 size={13} className="!text-white" />
              <h2 className="text-[11px] uppercase tracking-widest !text-white font-semibold">Team &amp; Ownership</h2>
              <span className="hidden sm:inline text-[9px] !text-[#B9DDF0]">Internal owners, specialists and partner attribution</span>
              {canEditOpp && (
              <button
                onClick={() => setShareOpen(true)}
                data-testid="share-btn"
                className="ml-auto px-2.5 py-1 rounded-full bg-[#E7F5FB] hover:bg-white !text-[#07539A] text-[11px] font-medium flex items-center gap-1"
              >
                <Users2 size={12} /> Manage team
              </button>
              )}
            </div>
            <div className="p-2">
              <TeamOwnershipTable entityType="opportunity" entityId={o.id} refreshKey={teamRefreshKey} />
            </div>
          </section>

          <MeetingLogInline entityType="opportunity" entityId={o.id} accent="navy" />

          <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_7px_20px_rgba(7,83,154,0.05)] [&>[data-testid='partner-assignment']]:border-0">
            <PartnerAssignmentPanel entityType="opportunity" entityId={o.id} accent="navy" />
          </div>
        </div>

        <aside className="space-y-3 min-w-0 lg:max-h-[1320px] lg:overflow-y-auto pr-1" data-testid="buying-committee-zone">
          <BuyingCommitteeEditor
            opportunity={o}
            readOnly={!canEditOpp}
            onSave={async (members) => {
              const { data } = await api.patch(`/opportunities/${id}`, { buying_committee: members });
              await load();
              return data;
            }}
          />
        </aside>
      </div>

      {/* Tasks */}
      <div className="border border-[#C9DCE6] rounded-[20px] bg-white overflow-hidden shadow-[0_7px_20px_rgba(7,83,154,0.06)]" data-testid="opp-tasks">
        <div className="px-3 py-2 border-b border-[#063F73] bg-[#07539A] flex items-center gap-2">
          <CheckSquare size={13} className="!text-white" />
          <div className="text-[11px] uppercase tracking-widest font-semibold !text-white">Open Tasks ({openTasks.length})</div>
        </div>
        {openTasks.length === 0 ? (
          <div className="p-6 text-[12px] text-[var(--text-muted)] text-center">No open tasks</div>
        ) : (
          <div className="divide-y divide-[#E1EBF0]">
            {openTasks.map(t => (
              <div key={t.id} className="px-4 py-3 flex items-center gap-3">
                <div className="w-1 h-1 rounded-full" style={{ background: t.priority === "critical" ? "var(--danger)" : t.priority === "high" ? "var(--copper)" : "var(--text-dim)" }} />
                <div className="text-[12px] flex-1">{t.title}</div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-dim)]">{t.status.replace("_", " ")}</div>
                <div className="text-[10px] font-mono text-[var(--text-muted)] w-20 text-right">{formatDate(t.due_date)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Share modal */}
      <ShareModal
        open={shareOpen}
        onOpenChange={(open) => {
          setShareOpen(open);
          if (!open) setTeamRefreshKey((key) => key + 1);
        }}
        entityType="opportunity"
        entityId={o.id}
        entityLabel={o.name}
        partnerAssignment
        onUpdated={() => setTeamRefreshKey((key) => key + 1)}
      />
      {customerOpen && company?.id && <CustomerCard companyId={company.id} onClose={() => setCustomerOpen(false)} />}

      <RequestDeletionModal
        open={deletionModalOpen}
        onOpenChange={setDeletionModalOpen}
        entityType="opportunity"
        entityId={o.id}
        entityLabel={company?.name || o.name}
      />
    </div>
  );
}
